import { useRef } from "react";
import { Quote, Star } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";

const TESTIMONIALS = [
  {
    quote:
      "Megjött a haj! Gyönyörű! 120%-osan meg vagyok elégedve, nagyon szépen köszönöm.",
    name: "Kármella",
    detail: "Ellenőrzött vásárlás",
    duration: "márc. 31.",
    image: "/testimonials/karmella.jpg",
    num: "01",
  },
  {
    quote:
      "Igen igen, eszméletlenül gyönyörű és egészséges! Köszönöm szépen!",
    name: "Lilla",
    detail: "Ellenőrzött vásárlás",
    duration: "2025. okt. 21.",
    image: "/testimonials/lilla.jpg",
    num: "02",
  },
  {
    quote:
      "Már átvettem, meseszép a haj — nagyon szépen köszönöm.",
    name: "Mária",
    detail: "Ellenőrzött vásárlás",
    duration: "Vásárlói visszajelzés",
    image: "/testimonials/maria.jpg",
    num: "03",
  },
];

export default function Testimonials() {
  const ref = useRef(null);
  useRevealAll(ref);

  return (
    <section
      ref={ref}
      data-testid="testimonials"
      className="bg-[color:var(--he-bg)] py-28 md:py-40 relative overflow-hidden"
    >
      {/* Decorative giant figure */}
      <div className="absolute -bottom-20 right-0 hidden md:block opacity-[0.04] pointer-events-none select-none">
        <span className="figure italic text-[28rem] leading-none text-[color:var(--he-ink)]">
          07
        </span>
      </div>

      <div className="he-container relative">
        {/* Heading */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 md:mb-28 items-end">
          <div className="md:col-span-5 reveal">
            <p className="overline mb-6 mag-marker">
              <span>Vásárlóink mondják</span>
            </p>
            <h2
              className="font-serif-display leading-[0.96] tracking-[-0.022em]"
              style={{ fontWeight: 400, fontSize: "clamp(2.4rem, 5.6vw, 5.4rem)" }}
            >
              Visszajelzések <br />
              <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                őszintén.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 reveal delay-1">
            <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.7] text-[15px]">
              Néhány vásárlónk tapasztalata — saját szavaikkal,
              hosszabb viselés után. Minden vélemény valódi, ellenőrzött
              rendelés alapján.
            </p>
          </div>
        </div>

        {/* Featured pullquote spanning full width */}
        <div className="relative mb-20 md:mb-28 reveal delay-2">
          <Quote
            className="text-[color:var(--he-gold)] absolute -top-6 -left-2 md:-left-4"
            size={56}
            strokeWidth={0.8}
          />
          <p
            className="font-serif-display italic text-[color:var(--he-ink)] leading-[1.12] tracking-[-0.012em] max-w-5xl pl-12 md:pl-20"
            style={{ fontWeight: 300, fontSize: "clamp(1.8rem, 4vw, 3.6rem)" }}
          >
            „A legszebb haj amit valaha{" "}
            <span className="text-[color:var(--he-gold)]">vásároltam.</span>"
          </p>
          <div className="mt-8 md:mt-10 pl-12 md:pl-20 flex items-center gap-5">
            <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
            <span className="overline-xs text-[color:var(--he-ink-soft)]">
              Rita · Ellenőrzött vásárlás
            </span>
          </div>
        </div>

        {/* 3-column editorial review cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {TESTIMONIALS.map((t, i) => (
            <ReviewCard key={t.num} t={t} index={i} />
          ))}
        </div>

        {/* Stats / Trust strip */}
        <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 border-y border-[color:var(--he-line)] py-12 reveal">
          <Stat figure="100" suffix="%" label="Valódi emberi haj" />
          <Stat figure="11" label="Aktuális tételek" />
          <Stat figure="2" label="Származási hely" />
          <Stat figure="2+" suffix=" év" label="Tipikus viselés" italic />
        </div>

      </div>
    </section>
  );
}

function ReviewCard({ t, index }) {
  return (
    <article
      className={`reveal group ${index === 1 ? "md:translate-y-10" : ""}`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`testimonial-card-${t.num}`}
    >
      {/* Image */}
      <div className="he-img-wrap aspect-[4/5] mb-7">
        <img
          src={t.image}
          alt={`${t.name} véleménye`}
          className="he-img w-full h-full object-cover"
          loading="lazy"
        />
        {/* Number marker */}
        <span className="absolute top-5 left-5 figure italic text-white text-3xl drop-shadow">
          {t.num}
        </span>
        {/* Stars */}
        <div className="absolute top-6 right-5 flex items-center gap-0.5 bg-black/30 backdrop-blur-sm px-2.5 py-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={10} fill="var(--he-gold)" stroke="var(--he-gold)" />
          ))}
        </div>
      </div>

      {/* Quote */}
      <Quote
        className="text-[color:var(--he-gold)] mb-4"
        size={20}
        strokeWidth={1}
      />
      <p
        className="font-serif-display italic leading-[1.4] text-[color:var(--he-ink)] mb-7"
        style={{ fontWeight: 400, fontSize: "1.18rem" }}
      >
        „{t.quote}"
      </p>

      {/* Author block */}
      <div className="border-t border-[color:var(--he-line)] pt-5 group-hover:border-[color:var(--he-gold)] transition-colors duration-500">
        <div className="flex items-baseline justify-between gap-3 mb-1.5">
          <p
            className="font-serif-display text-xl"
            style={{ fontWeight: 400 }}
          >
            {t.name}
          </p>
          <p className="overline-xs text-[color:var(--he-ink-soft)]" style={{ fontSize: "0.58rem" }}>
            {t.duration}
          </p>
        </div>
        <p className="text-xs text-[color:var(--he-ink-soft)] font-light leading-relaxed">
          {t.detail}
        </p>
      </div>
    </article>
  );
}

function Stat({ figure, suffix, label, italic }) {
  return (
    <div className="text-center md:text-left reveal">
      <p
        className={`font-serif-display text-4xl md:text-5xl leading-none ${italic ? "italic text-[color:var(--he-gold)]" : "text-[color:var(--he-ink)]"}`}
        style={{ fontWeight: 300 }}
      >
        {figure}
        {suffix && (
          <span className="text-2xl ml-1 text-[color:var(--he-ink-soft)]">{suffix}</span>
        )}
      </p>
      <p className="overline-xs text-[color:var(--he-ink-soft)] mt-3">{label}</p>
    </div>
  );
}
